import { DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR } from "@/constants";
import { WISHLIST_WIDGET_NAMES } from "@/constants/widget-names";
import { ModalManager } from "@/modal-manager";
import { customerInfoProviderToken, growaveConfigurationToken } from "@/tokens";
import { isShadowRoot } from "@/utils/isShadowRoot/isShadowRoot";
import { WishlistProductStatusesQuery } from "@apps/wishlist/queries/WishlistProductStatusesQuery";
import WishlistGuestInfoService from "@apps/wishlist/services/WishlistGuestInfoService";
import { WishlistProductStatusesService } from "@apps/wishlist/services/WishlistProductStatusesService";
import { WishlistsService } from "@apps/wishlist/services/WishlistsService";
import WishlistsStorefrontValuesService from "@apps/wishlist/services/WishlistsStorefrontValuesService";
import { CustomerInfoProviderInterface } from "@lib/CustomerInfoProvider/CustomerInfoProviderInterface";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { ProductIdManagerInterface } from "@lib/ProductIdManager/ProductIdManagerInterface";
import { ProductVariantIdManagerInterface } from "@lib/ProductVariantIdManager/ProductVariantIdManagerInterface";
import { AuthenticationModule } from "@modules/authentication/AuthenticationModule";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
import { I18nModule } from "@modules/i18n/I18nModule";
import { container } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";
import { WidgetInterface } from "@interfaces/WidgetInterface";

import AddToWishlistProductPageWidgetComponent from "./AddToWishlistProductPageWidget.svelte";
import { AddToWishlistProductPageWidgetConfigInterface } from "./AddToWishlistProductPageWidgetConfig";

class AddToWishlistProductPageWidget implements WidgetInterface {
    private readonly dynamicImportManager: DynamicImportManager;
    private readonly customerInfoProvider: CustomerInfoProviderInterface;

    constructor(
        private readonly config: AddToWishlistProductPageWidgetConfigInterface,
        private readonly logger: LoggerInterface,
        private readonly modalManager: ModalManager,
        private readonly productIdManager: ProductIdManagerInterface,
        private readonly productVariantIdManager: ProductVariantIdManagerInterface
    ) {
        this.logger.debug(
            "AddToWishlistProductPageWidget is initialized",
            config
        );

        this.dynamicImportManager =
            container.resolve(DynamicImportModule).exports.dynamicImportManager;
        this.customerInfoProvider = container.resolve(
            customerInfoProviderToken
        );

        const i18nModule = container.resolve(I18nModule);
        i18nModule.exports.i18n.registerLoader(
            "wishlist.AddToWishlistProductPageWidget",
            i18nModule.exports.i18nApiLoaderFactory.getLoader(
                "wishlist.AddToWishlistProductPageWidget"
            )
        );
        i18nModule.exports.i18n.registerLoader(
            "wishlist.critical.AddToWishlistProductPageWidget",
            i18nModule.exports.i18nCriticalLoaderFactory.getLoader(
                "wishlist.critical.AddToWishlistProductPageWidget"
            )
        );
    }

    async init(): Promise<this> {
        const wishlistsService = container.resolve(WishlistsService);
        const wishlistProductStatusesQuery = container.resolve(
            WishlistProductStatusesQuery
        );
        const wishlistsStorefrontValuesService = container.resolve(
            WishlistsStorefrontValuesService
        );
        const wishlistProductStatusesService = container.resolve(
            WishlistProductStatusesService
        );
        wishlistProductStatusesService.subscribe();
        const growaveConfiguration = container.resolve(
            growaveConfigurationToken
        );

        const element = this.config.target;
        element.style.display = "block";
        const isShadowRootDisabled = isShadowRoot(
            this.config.isWidgetShadowRootEnabled,
            growaveConfiguration.isShadowRootDisabledGlobally
        );

        const shadowRoot = isShadowRootDisabled
            ? element.shadowRoot || element.attachShadow({ mode: "open" })
            : null;

        const customizationManager = container.resolve(CustomizationManager);
        const authenticationInfoProvider =
            container.resolve(AuthenticationModule).exports
                .authenticationInfoProvider;

        const customizationManagerConfig = {
            widgetName:
                WISHLIST_WIDGET_NAMES.ADD_TO_WISHLIST_PRODUCT_PAGE_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromBlockCustomCss({
            element,
            shadowRoot,
            scriptSelector: DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR,
        });
        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );

        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        const wishlistGuestInfoService = container.resolve(
            WishlistGuestInfoService
        );

        new AddToWishlistProductPageWidgetComponent({
            target: shadowRoot || element,
            props: {
                productIdManager: this.productIdManager,
                productVariantIdManager: this.productVariantIdManager,
                wishlistsService,
                buttonClass: this.config.buttonClass,
                modalManager: this.modalManager,
                authenticationInfoProvider,
                wishlistProductStatusesQuery,
                wishlistProductStatusesService,
                dynamicImportManager: this.dynamicImportManager,
                wishlistsStorefrontValuesService,
                isShadowRootDisabled,
                wishlistGuestInfoService: wishlistGuestInfoService,
                customerInfoProvider: this.customerInfoProvider,
                afterWidgetInject: () => {
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    );
                },
            },
        });

        return this;
    }
}

export type AddToWishlistProductPageWidgetInterface =
    typeof AddToWishlistProductPageWidget;

export default AddToWishlistProductPageWidget;
